import CardCommon from '@/pages/opinion/components/CardCommon';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import GameStoreBarChart from './GameStoreBarChart';
import GameStoreLineChart from './GameStoreLineChart';
import { Segment } from 'tea-component';
import './index.scss';
import useGameStoreRequest from '../../hooks/useGameStoreRequest';
import { barIcon, lineIcon } from '@/pages/opinion/constants/chart';

export default function GameStoreChart({
  hasPcGame,
  hasConsoleGame,
  hasMobileGame,
  xAxisList,
  commonParams,
}: any) {
  const { formatMessage } = useIntl();
  const [operation, setOperation] = useState<string>('line');
  const [gameStore, setGameStore] = useState('google play');
  const [platform, setPlatform] = useState('pc');
  const [device, setDevice] = useState('Switch');

  const [gameStoreOptions, platformOptions] = useMemo(() => {
    const platformOptions = [];
    const gameStoreOptions = [];
    // 移动游戏，加上app store 和 google play
    hasMobileGame &&
      gameStoreOptions.push(
        { value: 'google play', text: 'Google Play' },
        { value: 'app store', text: 'App Store' },
      );

    // pc和console游戏，加上metacritic
    (hasConsoleGame || hasPcGame) &&
      gameStoreOptions.push({ value: 'metacritic', text: 'Metacritic' });

    // pc游戏，加上pc选项
    hasPcGame && platformOptions.push({ value: 'pc', text: 'PC' });

    // console游戏，加上console选项
    hasConsoleGame &&
      platformOptions.push({ value: 'console', text: 'Console' });

    // gameStoreOptions有一项就展示
    // platformOptions有两项才展示
    return [gameStoreOptions, platformOptions];
  }, [hasConsoleGame, hasPcGame, hasMobileGame]);

  const devicesOptions = useMemo(() => {
    return [
      { value: 'Switch', text: 'Switch' },
      { value: 'PlayStation 4', text: 'PlayStation 4' },
      { value: 'PC', text: 'PC' },
      { value: 'Xbox One', text: 'Xbox One' },
    ];
  }, [platform]);

  useEffect(() => {
    setGameStore(gameStoreOptions[0]?.value);
  }, [gameStoreOptions]);

  useEffect(() => {
    setPlatform(platformOptions[0]?.value || 'pc');
  }, [platformOptions]);

  const {
    lifetimeStacked,
    selectedStacked,
    requestGameStoreBar,
    isLoading,
    isError,
    requestGameStoreTimeline,
    isTimelineLoading,
    isTimelineError,
    lifetimeTrend,
    selectedTrend,
  } = useGameStoreRequest({
    xAxisList,
  });

  const init = async () => {
    requestGameStoreBar(commonParams, gameStore, platform, device);
    requestGameStoreTimeline(commonParams, gameStore, platform, device);
  };

  useEffect(() => {
    if (Object.keys(commonParams).length > 0 && xAxisList?.length > 0) {
      init();
    }
  }, [commonParams, gameStore, platform, xAxisList, !hasMobileGame, device]);

  return (
    <CardCommon
      className="dbo-display-card"
      title={formatMessage({ id: 'Game Store' })}
      defaultOperation={operation}
      handleOperationsOnClick={(item: any) => {
        setOperation(item.symbol);
      }}
      operations={[barIcon, lineIcon]}
    >
      <>
        <Segment
          className="display-segment"
          value={gameStore}
          onChange={(value) => setGameStore(value)}
          options={gameStoreOptions}
        />
        {platformOptions.length > 1 && gameStore === 'metacritic' && (
          <Segment
            className="display-segment"
            value={platform}
            onChange={(value) => setPlatform(value)}
            options={platformOptions}
          />
        )}

        {platform === 'console' && gameStore === 'metacritic' && (
          <Segment
            className="display-segment"
            value={device}
            onChange={(value) => setDevice(value)}
            options={devicesOptions}
          />
        )}

        {operation === 'bar' ? (
          <GameStoreBarChart
            selectedStacked={selectedStacked}
            commonParams={commonParams}
            xAxisList={xAxisList}
            gameStore={gameStore}
            lifetimeStacked={lifetimeStacked}
            platform={platform}
            loading={isLoading}
          ></GameStoreBarChart>
        ) : (
          <GameStoreLineChart
            xAxisList={xAxisList}
            commonParams={commonParams}
            loading={isTimelineLoading}
            selectedChartData={selectedTrend}
            lifetimeChartData={lifetimeTrend}
          ></GameStoreLineChart>
        )}
      </>
    </CardCommon>
  );
}
